// @givmo/compliance — what came back after a send, in words (CCM-R1-RESULTS-WEB; contract §10;
// D-CCM-68, REVIEW-30's frames a1–f5 and its words table, section 10).
//
// ══ HOW EVERY LINE SAYS WHERE IT CAME FROM ═════════════════════════════════════════════════════
//
// A person's record names who recorded it: "Maya Solberg recorded on Feb 1, 2027 that Maryland
// rejected it", never Maryland's own word. The email service's reports say "Givmo’s email service
// reports …", with no person. Three facts stay apart: "delivered to Maryland’s email server" is never
// "received", "acknowledged receiving it" is never "accepted", and a filing reads as filed only once
// someone attaches the state's confirmation.
//
// ══ WHOSE WORDS ═══════════════════════════════════════════════════════════════════════════════
//
// Every line in this file is APPROVED: D-CCM-68 (Jonathan 2026-10-12 21:57 PDT, "go with your rec";
// rulings log `scratchpad/2026-10-12-2157-results-screens-rulings-log.md`), each as REVIEW-30's
// section 10 words it. The approved lines it reuses keep their own rulings where they live: frames
// 66 to 71 (D-CCM-27), N6 and the support line (D-CCM-33), "Attach the confirmation" (slice 1b),
// "Send it yourself instead" (D-CCM-40), the note form (frame 38, D-CCM-22) and D-CCM-23's
// "Someone in your organization". One line since is D-CCM-79's (i) (Jonathan 2026-10-13 13:19 PDT,
// "approve recs"; rulings log `scratchpad/2026-10-13-1319-results-web-compositions-rulings-log.md`):
// `confirmationOnFileLine`.
//
// Pure: the page passes each name from the host's roster and each day in the filing's own zone.

import type {
  CaseFollowThrough,
  RecordedAgencyAnswer,
  Submission,
  SubmissionEvidence,
  SubmissionFollowThrough,
} from './compliance-submission-decode';
import { displayComplianceDate } from './compliance-decode';
import {
  shortDateIn,
  SOMEONE_IN_YOUR_ORGANIZATION,
  SOMEONE_IN_YOUR_ORGANIZATION_MID_SENTENCE,
  whenOf,
  type When,
} from './compliance-signing-model';
import { throughTheirAi } from './review-words';

// ── Words: the send page (section 1) ───────────────────────────────────────────────────────────

export const DELIVERY_REPORTS_AND_ANSWERS = 'Delivery reports and answers';
export const GIVMOS_EMAIL_SERVICE = 'Givmo’s email service';
export const ANSWERED = 'Answered';
export const THIS_ISNT_RIGHT = 'This isn’t right';
export const SEND_IT_AGAIN = 'Send it again';
export const PREPARE_THE_CORRECTED_FILING = 'Prepare the corrected filing';
export const ATTACH_THE_CONFIRMATION = 'Attach the confirmation';
export const YOU_CAN_SEND_IT_AGAIN = 'You can send it again, or send it yourself.';
/**
 * a7's line. On the face of a filing the organization sent itself (U1), its first sentence alone:
 * sending that again takes no authorization in Givmo.
 */
export const PREPARE_THE_CORRECTED_FILING_FIRST = 'To send it again, prepare the corrected filing.';
export const PREPARE_THE_CORRECTED_FILING_LINE = `${PREPARE_THE_CORRECTED_FILING_FIRST} It needs new signatures and a new authorization first.`;
export const ACCEPTED_FOR_DELIVERY = 'Accepted for delivery';

export function recordAnswerLabel(jurisdiction: string): string {
  return `Record ${jurisdiction}’s answer`;
}

export function itDidntReachLabel(jurisdiction: string): string {
  return `It didn’t reach ${jurisdiction}`;
}

export function receivingIsntAcceptingLine(jurisdiction: string): string {
  return `Receiving it isn’t accepting it. When ${jurisdiction}’s confirmation arrives, attach it.`;
}

/** What's known now, from the email service's own report about the agency's address. */
export function deliveredReportLine(jurisdiction: string, when: When): string {
  return `Givmo’s email service reports it was delivered to ${jurisdiction}’s email server on ${when.day} at ${when.time}.`;
}

export function refusedReportLine(jurisdiction: string, when: When): string {
  return `Givmo’s email service reports ${jurisdiction}’s email server refused it on ${when.day} at ${when.time}, so it didn’t arrive.`;
}

export function notHandedOnReportLine(jurisdiction: string, when: When): string {
  return `Givmo’s email service reports it didn’t hand the email on to ${jurisdiction}’s email server (${when.day} at ${when.time}), so it didn’t arrive.`;
}

/** What's known now, from a person's record of the agency's answer. */
export function answerRecordLine(
  answer: RecordedAgencyAnswer,
  name: string | undefined,
  day: string,
  jurisdiction: string,
  withMessage: boolean,
): string {
  const who = name ?? SOMEONE_IN_YOUR_ORGANIZATION;
  switch (answer) {
    case 'agency_acknowledgment':
      return withMessage
        ? `${who} recorded on ${day} that ${jurisdiction} acknowledged receiving it, and attached the message.`
        : `${who} recorded on ${day} that ${jurisdiction} acknowledged receiving it. No message from ${jurisdiction} is attached.`;
    case 'agency_rejection':
      return `${who} recorded on ${day} that ${jurisdiction} rejected it${withMessage ? ', and attached the message' : ''}.`;
    case 'agency_correction_request':
      return `${who} recorded on ${day} that ${jurisdiction} asked for changes${withMessage ? ', and attached the message' : ''}.`;
  }
}

export function confirmationAttachedLine(
  name: string | undefined,
  day: string,
  jurisdiction: string,
): string {
  return `${name ?? SOMEONE_IN_YOUR_ORGANIZATION} attached ${jurisdiction}’s confirmation on ${day}. The filing is recorded as filed.`;
}

/**
 * (i), APPROVED (D-CCM-79; CCM-R1-RESULTS-WEB-CONFIRMED-LINE): a9's place where nothing records who
 * attached the confirmation, or when, so no line names a person or a day.
 */
export function confirmationOnFileLine(jurisdiction: string): string {
  return `${jurisdiction}’s confirmation is on file. The filing is recorded as filed.`;
}

// ── Words: the list ────────────────────────────────────────────────────────────────────────────

/** The email service's report, as its row names it. */
export function reportLabel(
  kind: SubmissionEvidence['kind'],
  jurisdiction: string,
): string | undefined {
  switch (kind) {
    case 'provider_accepted':
      return ACCEPTED_FOR_DELIVERY;
    case 'provider_delivered':
      return `Delivered to ${jurisdiction}’s email server`;
    case 'provider_bounced':
      return `Refused by ${jurisdiction}’s email server`;
    case 'provider_dropped':
      return `Not handed on to ${jurisdiction}’s email server`;
    default:
      return undefined;
  }
}

/** A person's record, as its row says it: dated by the day it was recorded, the agency's own date in its words. */
export function answerRowLine(
  answer: RecordedAgencyAnswer,
  name: string | undefined,
  jurisdiction: string,
  occurredDay: string,
): string {
  const who = name ?? SOMEONE_IN_YOUR_ORGANIZATION;
  switch (answer) {
    case 'agency_acknowledgment':
      return `${who} recorded that ${jurisdiction} acknowledged receiving it on ${occurredDay}`;
    case 'agency_rejection':
      return `${who} recorded that ${jurisdiction} rejected it on ${occurredDay}`;
    case 'agency_correction_request':
      return `${who} recorded that ${jurisdiction} asked for changes on ${occurredDay}`;
  }
}

/** The answer under the row it answers (d3): the email service's report, or a person's record. */
export function answerUnderLine(
  onReport: boolean,
  name: string | undefined,
  day: string,
  jurisdiction: string,
  note: string,
): string {
  const who = name ?? SOMEONE_IN_YOUR_ORGANIZATION;
  return onReport
    ? `${who} recorded on ${day} that it didn’t reach ${jurisdiction}: “${note}”`
    : `${who} recorded on ${day} that this isn’t right: “${note}”`;
}

// ── Words: section 2's cards ───────────────────────────────────────────────────────────────────

export const REPORTED_AS_SENT = 'Reported as sent';

export function laterDeliveredLine(jurisdiction: string, when: When): string {
  return `Givmo’s email service later reported that it was delivered to ${jurisdiction}’s email server on ${when.day} at ${when.time}.`;
}

export function laterAcceptedLine(when: When): string {
  return `Givmo’s email service later reported that it accepted the email for delivery on ${when.day} at ${when.time}.`;
}

// ── Words: the dialogs (sections 3 and 4) ──────────────────────────────────────────────────────

export const WHICH_SEND = 'Which send';
export const RECORD_IT = 'Record it';
export const NOTE_OPTIONAL = 'Note (optional)';
export const OWN_ACCOUNT_HELPER =
  'Without it, this is recorded as your organization’s own account.';
export const WHAT_YOU_FOUND = 'What you found';
export const WHATS_WRONG = 'What’s wrong';
export const RECORD_THAT_IT_DIDNT_ARRIVE = 'Record that it didn’t arrive';
export const KEEP_YOUR_RECORD = 'Givmo will keep your record and show this beside it.';
export const IT_ACKNOWLEDGED = 'It acknowledged receiving it';
export const IT_REJECTED = 'It rejected it';
export const IT_ASKED_FOR_CHANGES = 'It asked for changes';
export const IT_ACCEPTED = 'It accepted it';

export function recordAnswerIntro(jurisdiction: string): string {
  return `Record what ${jurisdiction} told your organization about something it sent. It’s kept in your name, beside the sending record.`;
}

export function whatItSaidLabel(jurisdiction: string): string {
  return `What ${jurisdiction} said`;
}

export function answerDateLabel(jurisdiction: string): string {
  return `Date of ${jurisdiction}’s answer`;
}

/** The message's field: its label once a file is chosen, and the button that chooses one. */
export function attachMessageLabel(jurisdiction: string): string {
  return `Attach ${jurisdiction}’s message`;
}

export function attachMessageOptional(jurisdiction: string): string {
  return `Attach ${jurisdiction}’s message (optional)`;
}

export function acceptedNeedsConfirmationLine(jurisdiction: string): string {
  return `A filing is recorded as filed only with ${jurisdiction}’s confirmation attached.`;
}

export function emailSendOption(when: When): string {
  return `The email sent on ${when.day} at ${when.time}`;
}

export function selfSendOption(day: string, name: string | undefined): string {
  return `Sent yourself on ${day}, recorded by ${name ?? SOMEONE_IN_YOUR_ORGANIZATION_MID_SENTENCE}`;
}

/** The choice's form for a record made through the person's connected AI (D-CCM-96). */
export function selfSendOptionThroughAi(day: string, name: string | undefined): string {
  return `Sent yourself on ${day}, recorded through ${throughTheirAi(name)}`;
}

export function itDidntReachTitle(jurisdiction: string): string {
  return `It didn’t reach ${jurisdiction}?`;
}

/** d1: the act, then frame 69b's approved support sentence (D-CCM-33). */
export function itDidntReachIntro(jurisdiction: string): string {
  return `Givmo will record, in your name, that ${jurisdiction} didn’t get it, beside the email service’s report. You can then send it again from this page. If you’re not sure, ask Givmo support first, so ${jurisdiction} doesn’t get it twice.`;
}

export const THIS_ISNT_RIGHT_TITLE = 'This isn’t right?';

export function keepTheirRecordLine(name: string | undefined): string {
  return `Givmo will keep ${name ?? SOMEONE_IN_YOUR_ORGANIZATION_MID_SENTENCE}’s record and show yours beside it.`;
}

// ── Words: the panel and the Calendar (sections 5 and 6) ───────────────────────────────────────

/**
 * "Next due" and the Calendar row, in the sentence form. A past year's Calendar row leads with the
 * approved "Last report sent" (or "Last filing sent"; copy ruling item 1) in place of "Sent", and
 * then says no "again": the lead already names the last send.
 */
export function followThroughSentence(
  line: FollowThroughLine,
  jurisdiction: string,
  pastLead?: string,
): string {
  const lead = pastLead ?? 'Sent';
  switch (line.kind) {
    case 'not-delivered':
      return `${lead} ${line.sentOn}, but it didn’t reach ${jurisdiction}.`;
    case 'answer':
      return `${lead} ${line.sentOn}. ${answerTileLine(line, jurisdiction)}.`;
    case 'uncertain':
      return `Givmo can’t tell whether the ${line.sentOn} email went.`;
    case 'waiting':
      return line.again && pastLead === undefined
        ? `Sent again ${line.sentOn}, waiting for ${jurisdiction}’s confirmation.`
        : `${lead} ${line.sentOn}, waiting for ${jurisdiction}’s confirmation.`;
  }
}

/** The year's tile, in its own grammar: its parts, joined with " · ". */
export function followThroughTileParts(line: FollowThroughLine, jurisdiction: string): string[] {
  switch (line.kind) {
    case 'not-delivered':
      return [`Sent ${line.sentOn}, but it didn’t reach ${jurisdiction}`];
    case 'answer':
      return [`Sent ${line.sentOn}`, answerTileLine(line, jurisdiction)];
    case 'uncertain':
      return [`Givmo can’t tell whether the ${line.sentOn} email went`];
    case 'waiting':
      return [
        line.again ? `Sent again ${line.sentOn}` : `Sent ${line.sentOn}`,
        `waiting for ${jurisdiction}’s confirmation`,
      ];
  }
}

function answerTileLine(
  line: Extract<FollowThroughLine, { kind: 'answer' }>,
  jurisdiction: string,
): string {
  const who = line.recorder ?? SOMEONE_IN_YOUR_ORGANIZATION;
  return line.answer === 'rejected'
    ? `${who} recorded on ${line.recordedOn} that ${jurisdiction} rejected it`
    : `${who} recorded on ${line.recordedOn} that ${jurisdiction} asked for changes`;
}

// ── Reading the follow-through ─────────────────────────────────────────────────────────────────

/**
 * What a year's line says about its latest send: an exception, the ordinary wait (first or again),
 * or nothing new (`undefined`: confirmed, or a state this build doesn't know, which keeps today's
 * line, never a more confident one).
 */
export type FollowThroughLine =
  | { kind: 'not-delivered'; sentOn: string }
  | {
      kind: 'answer';
      answer: 'rejected' | 'correction_requested';
      sentOn: string;
      recorder: string | undefined;
      recordedOn: string;
    }
  | { kind: 'uncertain'; sentOn: string }
  | { kind: 'waiting'; sentOn: string; again: boolean };

export function followThroughLine(
  row: CaseFollowThrough | undefined,
  nameOf: (userId: string | undefined) => string | undefined,
): FollowThroughLine | undefined {
  if (row === undefined) return undefined;
  const sentOn = displayComplianceDate(row.sentOn);
  const latest = row.latest;
  switch (latest.state) {
    case 'not_delivered':
      return { kind: 'not-delivered', sentOn };
    case 'rejected':
    case 'correction_requested':
      return {
        kind: 'answer',
        answer: latest.state,
        sentOn,
        recorder: nameOf(latest.recordedByUserId),
        recordedOn:
          latest.recordedOn !== undefined ? displayComplianceDate(latest.recordedOn) : '—',
      };
    case 'uncertain':
      return { kind: 'uncertain', sentOn };
    case 'taken':
    case 'recorded_sent':
    case 'delivered':
    case 'acknowledged':
      return { kind: 'waiting', sentOn, again: row.earlierSends > 0 };
    default:
      return undefined;
  }
}

/**
 * A filing's latest send of any year, where it speaks for the filing's last recorded act (the
 * Calendar's last send; Filings' Last filing cell, U3): no older than what the register last
 * recorded. A confirmed send says nothing here: the register's "Filed …" already does.
 */
export function lastSendFollowThrough(
  lastActOn: string | undefined,
  send: CaseFollowThrough | undefined,
  nameOf: (userId: string | undefined) => string | undefined,
): FollowThroughLine | undefined {
  if (send === undefined || (lastActOn !== undefined && lastActOn > send.sentOn)) return undefined;
  return followThroughLine(send, nameOf);
}

/** Whether a line is an exception, which reads at body weight with its step (sections 5 and 6). */
export function isException(line: FollowThroughLine | undefined): boolean {
  return line !== undefined && line.kind !== 'waiting';
}

/** The record a follow-through names, among the case's evidence. */
export function decidingEvidence(
  followThrough: SubmissionFollowThrough,
  evidence: readonly SubmissionEvidence[],
): SubmissionEvidence | undefined {
  return followThrough.evidenceId !== undefined
    ? evidence.find((row) => row.evidenceId === followThrough.evidenceId)
    : undefined;
}

/**
 * A record's day as the backend serves it, in its record's own time zone, written as served
 * (CCM-R1-RESULTS-ANSWER-DAY): the same for every viewer. A read that serves none (an older
 * backend) is dated as before, by the instant in the page's zone.
 */
export function servedDay(
  day: string | undefined,
  instant: string | undefined,
  zone: string | undefined,
): string {
  return day !== undefined ? displayComplianceDate(day) : shortDateIn(instant, zone);
}

/** The day a record was recorded, as the page dates it: the served day in the filing's zone. */
export function recordedDay(
  followThrough: SubmissionFollowThrough,
  evidence: SubmissionEvidence | undefined,
  zone: string | undefined,
): string {
  if (followThrough.recordedOn !== undefined)
    return displayComplianceDate(followThrough.recordedOn);
  return servedDay(evidence?.recordedOn, evidence?.recordedAt, zone);
}

// ── The send page: one send's line, its next step and its list (section 1) ─────────────────────
//
// The page shows the newest send (Q1): its follow-through, its reports and records. Each reading
// here is the backend's own (contract §10; `app/domain/charity_compliance_submission.py`), so the
// page never offers what the backend refuses: only the email service's reports about the agency's
// own address count, and a row someone answered counts nowhere.

/** One send: an email (`attemptId`), or the organization's own record of sending (`handoffId`). */
export type ShownSend = { attemptId: string } | { handoffId: string };

/** The email service's report about the agency's own address: the only reports that count. */
export function isDestinationReport(row: SubmissionEvidence): boolean {
  return row.source === 'provider_event' && row.recipientRole === 'destination';
}

/** A person's record of the agency's answer (the record act's three kinds; never acceptance). */
export function isAgencyAnswer(kind: SubmissionEvidence['kind']): kind is RecordedAgencyAnswer {
  return (
    kind === 'agency_acknowledgment' ||
    kind === 'agency_rejection' ||
    kind === 'agency_correction_request'
  );
}

/** Every report and record about one send, as recorded and in the read's order (oldest first). */
export function evidenceAbout(
  evidence: readonly SubmissionEvidence[],
  send: ShownSend,
): SubmissionEvidence[] {
  return evidence.filter((row) =>
    'attemptId' in send ? row.attemptId === send.attemptId : row.handoffId === send.handoffId,
  );
}

/** What is known so far about one send, where the read serves it. */
export function followThroughOf(
  submission: Submission,
  send: ShownSend,
): SubmissionFollowThrough | undefined {
  return submission.followThrough.find((entry) =>
    'attemptId' in send ? entry.attemptId === send.attemptId : entry.handoffId === send.handoffId,
  );
}

/** The rows that still say something: neither an answer, nor a row an answer says is wrong. */
export function standingEvidence(rows: readonly SubmissionEvidence[]): SubmissionEvidence[] {
  const answered = new Set(
    rows.flatMap((row) => (row.answersEvidenceId !== undefined ? [row.answersEvidenceId] : [])),
  );
  return rows.filter(
    (row) =>
      row.answersEvidenceId === undefined &&
      row.answeredByEvidenceId === undefined &&
      !answered.has(row.evidenceId),
  );
}

/** The email service's standing reports that the agency's address took the email. */
export function takingReports(rows: readonly SubmissionEvidence[]): SubmissionEvidence[] {
  return standingEvidence(rows).filter(
    (row) =>
      isDestinationReport(row) &&
      (row.kind === 'provider_accepted' || row.kind === 'provider_delivered'),
  );
}

/**
 * Section 2: whether an email the email service didn't take, or one a person recorded as never
 * arriving, went after all, as the resend rule reads it (contract §8.2's final reading,
 * `counts_as_taken`): an agency's answer about it stands, or a report that the agency's address took
 * it stands while none says the address never got it. Its card is then "Reported as sent", and it
 * offers no send the backend would refuse (`ALREADY_SENT`; cut 1008-22's F3).
 */
export function wentAfterAll(rows: readonly SubmissionEvidence[]): boolean {
  const live = standingEvidence(rows);
  if (live.some((row) => isAgencyAnswer(row.kind))) return true;
  const destination = live.filter(isDestinationReport);
  return (
    destination.some(
      (row) => row.kind === 'provider_accepted' || row.kind === 'provider_delivered',
    ) &&
    !destination.some((row) => row.kind === 'provider_bounced' || row.kind === 'provider_dropped')
  );
}

/**
 * The section 2 card's report line: the newest standing report that the agency's address got it,
 * else that the email service accepted it for delivery.
 */
export function laterReportLine(
  rows: readonly SubmissionEvidence[],
  jurisdiction: string,
  zone: string | undefined,
): string | undefined {
  const taking = takingReports(rows);
  const delivered = taking.filter((row) => row.kind === 'provider_delivered').at(-1);
  if (delivered !== undefined)
    return laterDeliveredLine(jurisdiction, whenOf(delivered.occurredAt, zone));
  const accepted = taking.at(-1);
  return accepted !== undefined ? laterAcceptedLine(whenOf(accepted.occurredAt, zone)) : undefined;
}

/** The line under the card's own lines saying what's known now (a1 has none: nothing more is). */
export function knownNowLine(
  followThrough: SubmissionFollowThrough,
  evidence: readonly SubmissionEvidence[],
  jurisdiction: string,
  zone: string | undefined,
  nameOf: (userId: string | undefined) => string | undefined,
): string | undefined {
  const row = decidingEvidence(followThrough, evidence);
  switch (followThrough.state) {
    case 'delivered':
      return row !== undefined
        ? deliveredReportLine(jurisdiction, whenOf(row.occurredAt, zone))
        : undefined;
    case 'not_delivered':
      if (row === undefined) return undefined;
      return row.kind === 'provider_dropped'
        ? notHandedOnReportLine(jurisdiction, whenOf(row.occurredAt, zone))
        : refusedReportLine(jurisdiction, whenOf(row.occurredAt, zone));
    case 'acknowledged':
    case 'rejected':
    case 'correction_requested':
      return row !== undefined && isAgencyAnswer(row.kind)
        ? answerRecordLine(
            row.kind,
            nameOf(followThrough.recordedByUserId),
            recordedDay(followThrough, row, zone),
            jurisdiction,
            row.source === 'agency_message',
          )
        : undefined;
    case 'confirmed':
      // The read serves the day only with the person who recorded it (`recorded_on` with
      // `recorded_by_user_id`): with neither, (i)'s line. With a day but a person the roster can't
      // name, a9's line says "Someone in your organization" (D-CCM-23).
      return followThrough.recordedOn === undefined
        ? confirmationOnFileLine(jurisdiction)
        : confirmationAttachedLine(
            nameOf(followThrough.recordedByUserId),
            recordedDay(followThrough, undefined, zone),
            jurisdiction,
          );
    default:
      return undefined;
  }
}

/**
 * "What happens next", by what came back: the ordinary wait (frame 66's line); a send that didn't
 * arrive goes again by a person's new act; an acknowledgment waits for the confirmation; a rejection
 * or a request for changes needs the corrected filing. With the confirmation on file, nothing
 * (`undefined`). A state this build doesn't know reads as the ordinary wait, never as more.
 */
export type NextStep = 'wait' | 'send-again' | 'attach-confirmation' | 'prepare-corrected';

export function nextStepOf(state: SubmissionFollowThrough['state']): NextStep | undefined {
  switch (state) {
    case 'not_delivered':
      return 'send-again';
    case 'acknowledged':
      return 'attach-confirmation';
    case 'rejected':
    case 'correction_requested':
      return 'prepare-corrected';
    case 'confirmed':
      return undefined;
    default:
      return 'wait';
  }
}

/**
 * One row of "Delivery reports and answers": the email service's report about the agency's
 * address (its label), or a person's record of the agency's answer, with the record that says it is
 * wrong, if one does. Reports about another address and kinds this build doesn't know count
 * nowhere, so they aren't listed.
 */
export interface DeliveryListRow {
  row: SubmissionEvidence;
  report?: string;
  answer?: RecordedAgencyAnswer;
  answeredBy?: SubmissionEvidence;
}

export function deliveryListRows(
  rows: readonly SubmissionEvidence[],
  jurisdiction: string,
): DeliveryListRow[] {
  const answers = new Map(
    rows.flatMap((row) =>
      row.answersEvidenceId !== undefined ? [[row.answersEvidenceId, row] as const] : [],
    ),
  );
  return rows.flatMap((row): DeliveryListRow[] => {
    if (row.answersEvidenceId !== undefined) return [];
    const answeredBy =
      answers.get(row.evidenceId) ??
      rows.find((other) => other.evidenceId === row.answeredByEvidenceId);
    const under = answeredBy !== undefined ? { answeredBy } : {};
    if (isDestinationReport(row)) {
      const report = reportLabel(row.kind, jurisdiction);
      return report !== undefined ? [{ row, report, ...under }] : [];
    }
    if (row.source !== 'provider_event' && isAgencyAnswer(row.kind)) {
      return [{ row, answer: row.kind, ...under }];
    }
    return [];
  });
}

/** One send the agency's answer may be recorded about (c1's "Which send"). */
export interface SendOption {
  key: string;
  send: ShownSend;
  label: string;
}

/**
 * Every send that went out, newest first (the backend's `went_out`): an email the email service
 * took, or any it reported on, answered or not, so a wrong answer has its way back when the agency's
 * letter arrives; and every record of sending.
 */
export function sendsThatWentOut(
  submission: Submission,
  zone: string | undefined,
  nameOf: (userId: string | undefined) => string | undefined,
): SendOption[] {
  const emails = submission.attempts
    .filter(
      (attempt) =>
        attempt.state === 'provider_accepted' ||
        submission.evidence.some(
          (row) => row.attemptId === attempt.attemptId && row.source === 'provider_event',
        ),
    )
    .map((attempt) => ({
      at: attempt.committedAt,
      option: {
        key: `attempt:${attempt.attemptId}`,
        send: { attemptId: attempt.attemptId },
        label: emailSendOption(whenOf(attempt.committedAt, zone)),
      },
    }));
  const handoffs = submission.handoffs.map((handoff) => ({
    at: handoff.recordedAt,
    option: {
      key: `handoff:${handoff.handoffId}`,
      send: { handoffId: handoff.handoffId },
      label: (handoff.recordedChannel === 'connector' ? selfSendOptionThroughAi : selfSendOption)(
        displayComplianceDate(handoff.sentOn),
        nameOf(handoff.recordedByUserId),
      ),
    },
  }));
  const time = (at: string | undefined) => (at !== undefined ? Date.parse(at) : 0) || 0;
  return [...emails, ...handoffs]
    .sort((left, right) => time(right.at) - time(left.at))
    .map((each) => each.option);
}
